import { useState, useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge } from '../../components/ui';
import { motion } from 'framer-motion';
import {
  areNotificationsEnabled,
  checkNotificationPermission,
  openNotificationSettings,
} from '@/core/notifications';
import { getNativePlatform } from '@/core/capacitor';
import { useAuth } from '@/core/AuthContext';

export function NotificationSection() {
  const { userData } = useAuth();
  const isClient = userData?.role === 'client';

  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [notificationLoading, setNotificationLoading] = useState(true);

  useEffect(() => {
    const supported = getNativePlatform() === 'ios';
    setNotificationsSupported(supported);

    if (!supported) {
      setNotificationsEnabled(false);
      setNotificationPermission('prompt');
      setNotificationLoading(false);
      return;
    }

    let cancelled = false;
    let removeResumeListener: (() => void) | undefined;

    const loadNotificationStatus = async () => {
      setNotificationLoading(true);

      try {
        const [enabled, permission] = await Promise.all([
          areNotificationsEnabled(),
          checkNotificationPermission(),
        ]);

        if (!cancelled) {
          setNotificationsEnabled(enabled);
          setNotificationPermission(permission);
        }
      } catch (error) {
        console.error('Failed to load notification status:', error);
        if (!cancelled) {
          setNotificationsEnabled(false);
          setNotificationPermission('prompt');
        }
      } finally {
        if (!cancelled) {
          setNotificationLoading(false);
        }
      }
    };

    void loadNotificationStatus();

    void CapacitorApp.addListener('resume', () => {
      void loadNotificationStatus();
    }).then((handle) => {
      removeResumeListener = () => {
        void handle.remove();
      };
    });

    return () => {
      cancelled = true;
      removeResumeListener?.();
    };
  }, []);

  const notificationBadge = !notificationsSupported
    ? 'Not Supported'
    : notificationLoading
      ? 'Checking'
      : notificationsEnabled
        ? 'Enabled'
        : 'Disabled';

  const notificationBadgeVariant = !notificationsSupported
    ? 'default'
    : notificationsEnabled
      ? 'success'
      : 'warning';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 }}
      className={isClient ? 'order-[-10]' : undefined}
    >
      <Card variant="elevated" padding="lg">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>Workout start reminders in the native iOS app</CardDescription>
            </div>
            <Badge variant={notificationBadgeVariant}>{notificationBadge}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-slate-400">
            {!notificationsSupported ? (
              <p>Only available in iOS app.</p>
            ) : notificationsEnabled ? (
              <p>You&apos;ll get local reminders when your workout is about to start.</p>
            ) : (
              <p>
                Notifications are off{notificationPermission === 'denied' ? ' in iOS Settings.' : '.'}
              </p>
            )}

            {notificationsSupported && !notificationsEnabled && (
              <Button variant="outline" onClick={() => void openNotificationSettings()}>
                Open iOS Settings
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
